import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FullscreenOverlay, {
  FullscreenOverlayProps,
} from './FullscreenOverlay';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import {
  Button,
  ButtonBase,
  IconButton,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { SectionTitle } from 'pages/MainPage';

import {
  ArrowForwardRounded,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@material-ui/icons';
import {
  ApiAttachmentForFS,
  FormattedMainFile,
} from 'store/models/MainFiles';
import {
  ticketDataSelector,
  userDataSelector,
} from 'store/selectors';
import { FileSelected } from 'store/models/file-selected/FileSelected';
import { Ticket as TicketModel } from 'store/models/Ticket';
import { EachFileSelectedWithThumbnail } from 'store/models/file-selected/file-selected.interface';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { dndConstants } from 'store/constants/dnd.constants';
import {
  alertsActions,
  curBookingsActions,
  fileSelectedActions,
  previewFileActions,
  showOverlayActions,
  ticketActions,
} from 'store/actions';
import AttachmentListWrapper from './file-selection/AttachmentListWrapper';
import DestThumbnailList from './file-selection/DestThumbnailList';
import SrcFile from './file-selection/SrcFile';
import CommonOrSplitBox from './file-selection/CommonOrSplitBox';
import {
  hasAnyAddableFileSelectedSelector,
  hasAnyTrueAddableFileSelectedSelector,
  isLoadingPreviewBookingsAtViewingBookingSelector,
} from 'store/selectors/cur-bookings-attachments.selector';
import { getCurFileSelectedSelector } from 'store/selectors/cur-bookings-attachments.selector';
import { fileSelectedWithThumbnailSelector } from 'store/selectors/cur-bookings-attachments.selector';
import { renderFileImage } from '../main/MainFiles';
import EditableBookingNoMenu from './file-selection/EditableBookingNoMenu';
import { isEmpty } from 'utils/empty';
import GlobalCommon from './file-selection/GlobalCommon';
import { MatUiCheckbox } from '../main';
import { attachmentsSelector } from 'store/selectors/ticket-attachment.selector';
import { BookingPreviewsHelper } from 'store/models/BookingPreviews';
import { getViewingBookingSelector } from 'store/selectors/cur-bookings.selector';
import { BookingI } from 'store/models/bookings/Booking';
import { ticketFormTicketSelector } from 'store/selectors/ticket.selector';

const overlayName = 'FileSelection';
interface FileSelectionOverlayProps extends FullscreenOverlayProps {}

const MAX_THREE_COLS_FILES = 8 * 3;

const FileSelectionOverlay: React.FC<FileSelectionOverlayProps> = ({
  children,
  ...props
}) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const viewingBooking: BookingI = useSelector(
    getViewingBookingSelector,
  );

  const viewingBookingNo: string = useMemo(
    () => viewingBooking?.bookingNo,
    [viewingBooking],
  );
  const isLoadingPreviewBookingsAtViewingBooking: boolean = useSelector(
    isLoadingPreviewBookingsAtViewingBookingSelector,
  );
  const formTicket = useSelector(ticketFormTicketSelector); // current form ticket
  const authUser = useSelector(userDataSelector);
  const canSubmitPreview = useMemo(
    () =>
      TicketModel.canNonAdminEditTicket(formTicket?.ticket, authUser),
    [formTicket, authUser],
  );

  const fileSelectedWithThumbnail: EachFileSelectedWithThumbnail[] = useSelector(
    fileSelectedWithThumbnailSelector,
  );
  const fileSelectedNoThumbnail = useSelector(
    getCurFileSelectedSelector,
  );

  const hasAnyAddableFileSelected = useSelector(
    hasAnyAddableFileSelectedSelector,
  );

  const hasAnyTrueAddableFileSelected = useSelector(
    hasAnyTrueAddableFileSelectedSelector,
  );

  const ticketData = useSelector(ticketDataSelector);
  const ticketBodyPreviewUrl =
    ticketData?.messageInfo?.bodyPreview?.url;

  const hasAnyDestFileSelected = useMemo(() => {
    return fileSelectedNoThumbnail.some((f) => f.isSelected);
  }, [fileSelectedNoThumbnail]);

  // available attachments only
  const attachments = useSelector(attachmentsSelector);

  const partIdToAtt = useMemo(
    () =>
      attachments.reduce(
        (acc, att) => ({ ...acc, [att.partId]: att }),
        {},
      ),
    [attachments],
  );

  const [isSelectAllFiles, setIsSelectAllFiles] = useState(false);

  const [isSelectAllDestFiles, setIsSelectAllDestFiles] = useState(
    false,
  );

  useEffect(() => {
    const isAllDestSelected = new FileSelected(
      fileSelectedWithThumbnail,
    ).isAllFileSelectedSelected();
    if (isAllDestSelected !== isSelectAllDestFiles) {
      setIsSelectAllDestFiles(isAllDestSelected);
    }
  }, [fileSelectedWithThumbnail, isSelectAllDestFiles]);

  const [selectedFilePartIds, setSelectedFilePartIds] = useState<
    string[]
  >([]);

  const handlePreviewFile = (file: FormattedMainFile) => {
    console.debug(`file: `, file);
    dispatch(previewFileActions.previewFile(file));
  };

  const handleReset = () => {
    console.debug(`handle Reset`);
    dispatch(fileSelectedActions.resetFileSelected());
  };
  const handleDone = () => {
    console.debug('handle done');
    // trigger auto save
    dispatch(ticketActions.postAutosaveCurrentFormTicket());
    handleClose();
  };

  const attachmentLength = attachments.length;
  const attachmentsRef = useRef(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const handleSetSelectAllFiles = useCallback((val: boolean) => {
    setIsSelectAllFiles(val);
    let updated;
    if (val) {
      updated = attachmentsRef.current.map((att) => att.partId);
    } else {
      updated = [];
    }
    setSelectedFilePartIds(updated);
  }, []);

  /**
   * auto select all when attachments length change
   */
  useEffect(() => {
    if (attachmentLength) {
      handleSetSelectAllFiles(true);
    }
  }, [attachmentLength, handleSetSelectAllFiles]);

  /**
   * when selectedFilePartId changes, check if all files are selected -> then check is SelectAllFiles
   */
  useEffect(() => {
    if (
      attachments.length &&
      selectedFilePartIds.length === attachments.length
    ) {
      setIsSelectAllFiles(true);
    } else {
      setIsSelectAllFiles(false);
    }
  }, [selectedFilePartIds, attachments]);

  const handleToggleSelectFile = (partId: string) => {
    let updated;
    if (isPartIdSelected(partId)) {
      updated = selectedFilePartIds.filter((pI) => pI !== partId);
    } else {
      updated = selectedFilePartIds.concat(partId);
    }
    setSelectedFilePartIds(updated);
  };

  const isPartIdSelected = (partId: string) => {
    return selectedFilePartIds.findIndex((pI) => pI === partId) > -1;
  };

  const selectedAttsToShow = useMemo<ApiAttachmentForFS[]>(() => {
    return selectedFilePartIds
      .filter((partId) => partId in partIdToAtt)
      .map((partId) => partIdToAtt[partId]);
  }, [partIdToAtt, selectedFilePartIds]);

  const handleSelectDestFile = (
    destFileIndex: number,
    checked: boolean,
  ) => {
    dispatch(
      fileSelectedActions.setSelectFileAtIndex(
        destFileIndex,
        checked,
      ),
    );
  };

  const handleSetSelectAllDestFiles = (checked: boolean) => {
    setIsSelectAllDestFiles(checked);
    dispatch(fileSelectedActions.setSelectFileAtAllIndexes(checked));
  };

  const handleRemoveDestFileAtIndex = (index: number) => {
    dispatch(fileSelectedActions.removeFileAtIndex(index));
  };

  const handleRemoveAllSelectedFiles = () => {
    dispatch(fileSelectedActions.removeAllSelectedFiles());
  };

  /**
   * use selected files
   */
  const handleUseAllSelectedFiles = () => {
    dispatch(
      fileSelectedActions.useManySrcFiles(
        selectedAttsToShow,
        isSelectAllDestFiles,
      ),
    );
  };

  const [editFilenameIndex, setEditFilenameIndex] = useState<
    null | number
  >(null);

  /**
   * destination file
   * @param fileIndex
   */
  const handleEditFilename = (fileIndex: number) => {
    setEditFilenameIndex(fileIndex);
  };

  const handleRenameFileOnBlur = (index: number, newName: string) => {
    if (isEmpty(newName)) {
      dispatch(alertsActions.error(`Filename cannot be empty`));
      return;
    }
    // TODO: consider adding validating filename
    dispatch(fileSelectedActions.renameFileAtIndex(index, newName));
    setEditFilenameIndex(null);
  };

  const handleClose = () => {
    dispatch(showOverlayActions.close(overlayName));
    dispatch(curBookingsActions.setViewingBookingIndex(null));
  };

  const handleViewOriginalEmail = () => {
    if (ticketBodyPreviewUrl) {
      dispatch(
        previewFileActions.show(
          ticketBodyPreviewUrl,
          'application/pdf',
        ),
      );
    }
  };

  const handlePreviewFileSelected = async (
    eachFileSelected: EachFileSelectedWithThumbnail,
    index: number,
  ) => {
    if (!eachFileSelected.canSelectPage) {
      // **** move to do in useEffect *******
      // handle excel
      const { partId } = eachFileSelected;
      if (partId) {
        if (partId in partIdToAtt) {
          const att = partIdToAtt[partId];
          dispatch(previewFileActions.previewFile(att));
        } else {
          // but won't throw just in case
          throw Error(
            `couldn't find the ${partId} partId in partIdToAtt`,
          );
        }
      } else {
        throw Error(`partId is undefined`);
      }
    } else {
      if (canSubmitPreview) {
        const message = 'Generating files for preview. Please wait.';
        const previewResult = await dispatch(
          ticketActions.submitPreviewBookingIfHasChange(
            () => dispatch(alertsActions.info(message, true)),
            () => dispatch(alertsActions.clear({ message })),
          ),
        );
        if (previewResult) {
          const previewedFile = BookingPreviewsHelper.getPreviewResultAtFileIndexAtBookingNo(
            previewResult,
            index,
            viewingBookingNo,
          );
          dispatch(previewFileActions.previewFile(previewedFile));
        } else {
          dispatch(alertsActions.clear({ message }));
          dispatch(alertsActions.error('Unable to preview file'));
        }
      } else {
        // dispatch preview starting from index 0
        const allPages = eachFileSelected.pages.map((p) => ({
          url: p.thumbnail.pdfUrl,
          mimeType: 'application/pdf',
        }));
        dispatch(
          previewFileActions.showPdfWithPagination(0, allPages),
        );
      }
    }
  };

  // TODO: consider create render function instead of memoizing the className here
  const attNameClassName = useMemo(
    () =>
      isLoadingPreviewBookingsAtViewingBooking ? 'disabled' : 'link',
    [isLoadingPreviewBookingsAtViewingBooking],
  );

  return (
    <FullscreenOverlay noAppBar {...props} handleClose={handleClose}>
      <DndProvider backend={HTML5Backend}>
        <SRootWrapper>
          <SPageNameWrapper>
            <SectionTitle title="File Selection Page" />
          </SPageNameWrapper>
          <SWrapper>
            <SLeftOrRight>
              <FileSectionHeader>
                <SectionTitle
                  title="Drag pages for this booking no."
                  withoutMarginBottom
                />
                <Tooltip
                  title={
                    <STooltipText>
                      Replace destination files with all source
                      attachments
                    </STooltipText>
                  }
                >
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => handleUseAllSelectedFiles()}
                  >
                    Use Selected Files
                  </Button>
                </Tooltip>
              </FileSectionHeader>
              {/* FileSection */}
              <FileSectionBodyAndSelectAll>
                {/* select all */}
                <MatUiCheckbox
                  value={isSelectAllFiles}
                  name={'isSelectAllFiles'}
                  label="Select All"
                  handleChange={(checked) =>
                    handleSetSelectAllFiles(checked)
                  }
                />
                <FileSectionBody
                  className={
                    attachments.length >= MAX_THREE_COLS_FILES
                      ? 'flow-row'
                      : 'flow-column'
                  }
                >
                  {attachments.map((att) => (
                    <SFile key={att.partId}>
                      <SFileIcon
                        className="clickable"
                        onClick={() =>
                          handleToggleSelectFile(att.partId)
                        }
                      >
                        {isPartIdSelected(att.partId) ? (
                          <SCheckIcon />
                        ) : (
                          renderFileImage(att)
                        )}
                      </SFileIcon>
                      {att.hasParent && (
                        <SIndent>
                          <SLine></SLine>
                        </SIndent>
                      )}
                      <SFileName
                        className="link"
                        onClick={() => handlePreviewFile(att)}
                        title={att.filename}
                      >
                        {att.filename}
                      </SFileName>
                    </SFile>
                  ))}
                </FileSectionBody>
              </FileSectionBodyAndSelectAll>
              <AllFilesThumbnailsWrapper>
                {/* filtered attachments */}
                {selectedAttsToShow.map((att) => (
                  <SrcFile
                    key={att.partId}
                    srcFile={att}
                    hasAnyAddableFileSelected={
                      hasAnyAddableFileSelected
                    }
                    hasAnyTrueAddableFileSelected={
                      hasAnyTrueAddableFileSelected
                    }
                  />
                ))}
              </AllFilesThumbnailsWrapper>
            </SLeftOrRight>
            <Tooltip title="Drag file or page from to the right box to create a new file">
              <SArrowForward />
            </Tooltip>
            <SLeftOrRight>
              <GlobalCommon />
              <SCommonSeparateWrapper>
                {[
                  {
                    title: 'Common',
                    descriptionTexts: [
                      {
                        text:
                          '(prepend to every output file when drag',
                      },
                      { text: 'file', highlighted: true },
                      { text: 'or' },
                      { text: 'page', highlighted: true },
                      { text: 'to this)' },
                    ],
                    // description:
                    //   'prepend to every output file when drag file or page to this',
                    dndItemType: dndConstants.COMMON,
                  },
                  {
                    title: 'Split',
                    descriptionTexts: [
                      {
                        text:
                          '(create new output file for each page when drag',
                      },
                      { text: 'file', highlighted: true },
                      { text: 'to this)' },
                    ],
                    // description:
                    //   'create new output file for each page when drag file to this',
                    dndItemType: dndConstants.SPLIT,
                  },
                ].map((item) => (
                  <CommonOrSplitBox
                    key={item.title}
                    title={item.title}
                    // description={item.description}
                    descriptionTexts={item.descriptionTexts}
                    dndItemType={item.dndItemType}
                  />
                ))}
              </SCommonSeparateWrapper>
              <SSelectAttachBar>
                <SSelectAttachBarLeft>
                  <MatUiCheckbox
                    value={isSelectAllDestFiles}
                    name={'isSelectAllDestFiles'}
                    label="Select All"
                    handleChange={(checked) =>
                      handleSetSelectAllDestFiles(checked)
                    }
                  />
                  <Tooltip title="Remove the selected files">
                    <SDeleteAllIconButton
                      className={hasAnyDestFileSelected ? '' : 'hide'}
                      onClick={() => handleRemoveAllSelectedFiles()}
                      color="primary"
                      size="small"
                    >
                      <DeleteIcon />
                    </SDeleteAllIconButton>
                  </Tooltip>
                </SSelectAttachBarLeft>
                <SSelectAttachBarRight>
                  {/* <BookingNoMenu /> */}
                  <EditableBookingNoMenu />
                </SSelectAttachBarRight>
              </SSelectAttachBar>
              {/* add the original email file? */}
              <AttachmentListWrapper
                isSelectAll={isSelectAllDestFiles}
              >
                {fileSelectedWithThumbnail.map(
                  (eachFileSelected, index) => (
                    <SAttachment key={eachFileSelected.filename}>
                      <SAttBar>
                        <SAttBarInside>
                          <MatUiCheckbox
                            handleChange={(checked) =>
                              handleSelectDestFile(index, checked)
                            }
                            value={eachFileSelected.isSelected}
                          />
                          <SAttNameAndEdit>
                            {index === editFilenameIndex ? (
                              <SAttNameInput
                                fullWidth
                                autoFocus
                                defaultValue={
                                  eachFileSelected.filename
                                }
                                onBlur={(e) =>
                                  handleRenameFileOnBlur(
                                    index,
                                    e.target.value,
                                  )
                                }
                                onKeyPress={(e: any) => {
                                  if (e.key === 'Enter')
                                    handleRenameFileOnBlur(
                                      index,
                                      e.target.value,
                                    );
                                }}
                              />
                            ) : (
                              <SAttName
                                className={attNameClassName}
                                onClick={(e) =>
                                  handlePreviewFileSelected(
                                    eachFileSelected,
                                    index,
                                  )
                                }
                                title={eachFileSelected.filename}
                              >
                                {eachFileSelected.filename}
                              </SAttName>
                            )}
                            {/* do not show the edit button if currently editing it */}
                            {index !== editFilenameIndex && (
                              <Tooltip title="Click to rename file">
                                <ButtonBase
                                  onClick={() =>
                                    handleEditFilename(index)
                                  }
                                  disabled={
                                    isLoadingPreviewBookingsAtViewingBooking
                                  }
                                >
                                  <EditIcon
                                    fontSize="small"
                                    color={
                                      isLoadingPreviewBookingsAtViewingBooking
                                        ? 'disabled'
                                        : undefined
                                    }
                                  />
                                </ButtonBase>
                              </Tooltip>
                            )}
                          </SAttNameAndEdit>
                        </SAttBarInside>
                        <SAttBarInsideDelete>
                          <IconButton
                            onClick={() =>
                              handleRemoveDestFileAtIndex(index)
                            }
                            color="primary"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </SAttBarInsideDelete>
                      </SAttBar>
                      {/* TODO: considering combine the render thumbnails function with the attachments for select */}
                      {eachFileSelected.canSelectPage && (
                        <DestThumbnailList
                          thumbnails={eachFileSelected.pages}
                          destFileIndex={index}
                        />
                      )}
                    </SAttachment>
                  ),
                )}
                {ticketBodyPreviewUrl && (
                  <SOriginalEmailWrapper>
                    <MatUiCheckbox value={true} disabled />
                    <SAttName
                      onClick={() => handleViewOriginalEmail()}
                      className={'link'}
                    >
                      OriginalEmail.pdf
                    </SAttName>
                    <IconButton disabled size="small">
                      <DeleteIcon />
                    </IconButton>
                  </SOriginalEmailWrapper>
                )}
              </AttachmentListWrapper>
            </SLeftOrRight>
          </SWrapper>
        </SRootWrapper>
      </DndProvider>
      {/* bottom footer */}
      <SFooter>
        {[
          {
            label: 'Reset',
            onClick: handleReset,
          },
          {
            label: 'Done',
            onClick: handleDone,
          },
        ].map(
          (
            b: {
              label: string;
              onClick: (event: React.MouseEvent) => void;
            },
            i,
          ) => (
            <Button
              key={b.label}
              variant="contained"
              color="primary"
              onClick={b.onClick}
            >
              {b.label}
            </Button>
          ),
        )}
      </SFooter>
    </FullscreenOverlay>
  );
};

export default FileSelectionOverlay;

const SRootWrapper = styled.div`
  overflow: hidden;
  margin: 1rem 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const SWrapper = styled.div`
  display: grid;
  flex: 1;

  grid-template-columns: 1fr auto 1fr;
  overflow: hidden;
  margin-bottom: 9vh;
  column-gap: 4px;
`;
const SFooter = styled.div`
  background: ${(props) => props.theme.palette.background.paper};
  position: absolute;
  height: 4.5rem;
  bottom: 0;
  left: 0;
  right: 0;
  box-shadow: ${(props) => props.theme.boxShadow.appBar};
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  & > .MuiButtonBase-root + .MuiButtonBase-root {
    margin-left: 1rem;
  }
`;
const SLeftOrRight = styled.div`
  border-radius: 0.3rem;
  background: ${(props) => props.theme.palette.background.paper};
  padding: 0.8rem;
  overflow-y: auto;
`;

const FileSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const FileSectionBody = styled.div.attrs((props) => ({
  className: props.className,
}))`
  display: grid;
  overflow: auto;
  row-gap: 2px;
  height: 20vh;
  grid-template-columns: repeat(3, minmax(auto, 33.3%));
  grid-template-rows: repeat(7, auto);
  &.flow-column {
    grid-auto-flow: column; // fill column first
  }
  &.flow-row {
    grid-auto-flow: row; // fill column first
  }
`;
const FileSectionBodyAndSelectAll = styled.div`
  box-shadow: ${(props) => props.theme.boxShadow.subtleAppBar};
  border-radius: 5px; // TODO: reconsider the unit
  padding: 0.5rem;
  margin-top: 0.5rem;
`;

const AllFilesThumbnailsWrapper = styled.div`
  margin-top: 1rem;
`;

const SCommonSeparateWrapper = styled.div`
  display: flex;
  column-gap: 0.3rem;
`;

const SSelectAttachBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;
  & .MuiButton-root {
    height: 1.6rem;
  }
`;

const SSelectAttachBarLeft = styled.div`
  display: flex;
  align-items: center;
  & > * + .MuiButton-root {
    margin-left: 1rem;
  }
  & > * + .MuiIconButton-root {
    margin-left: 0.5rem;
  }
`;
const SSelectAttachBarRight = styled.div`
  display: flex;
  align-items: center;
  & > * + * {
    margin-left: 0.3rem !important;
  }
`;

const SAttachment = styled.div``;
const SAttNameAndEdit = styled.div`
  display: flex;
  column-gap: 1rem;
`;
const SAttNameInput = styled(TextField)``;
const SAttName = styled.div.attrs((props) => ({
  className: props.className,
}))`
  font-size: ${(props) => props.theme.typography.fontSize.title};
  line-height: ${(props) => props.theme.typography.lineHeight.title};
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  &.link {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &.disabled {
    cursor: default;
    &:hover {
      text-decoration: default;
    }
    color: ${(props) => props.theme.palette.text.disabled};
  }
`;
const SAttBar = styled.div`
  display: flex;
  justify-content: space-between;
`;

// const SThumbnailAndPage = styled.div``;

// // all width height box-shawdow are copied from figmas
// const SThumbnail = styled.div`
//   width: 105px;
//   height: 133px;
//   box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
// `;
// const SPage = styled.div`
//   font-size: ${(props) => props.theme.typography.fontSize.body};
//   text-align: center;
//   margin-top: 0.3rem;
// `;
const SFile = styled.div`
  display: flex;
  align-items: center;
`;
const SFileIcon = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
  margin-left: 2px; // to match select all
`;
const SFileName = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  font-size: ${(props) => props.theme.typography.fontSize.body};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SCheckIcon = styled(CheckIcon)`
  &.MuiSvgIcon-root {
    font-size: ${(props) => props.theme.typography.fontSize.title};
    color: ${(props) => props.theme.palette.success.main};
  }
`;

const SAttBarInside = styled.div`
  display: grid;
  grid-template-columns: auto minmax(auto, 450px) auto;
`;
const SAttBarInsideDelete = styled.div``;

const STooltipText = styled.div`
  font-size: 13px;
`;

const SIndent = styled.div`
  margin-right: 5px;
  width: 0.5rem;
  display: grid;
  place-items: center;
`;
const SLine = styled.div`
  width: 100%;
  height: 1px;
  background: ${(props) => props.theme.palette.grey[300]};
`;

const SPageNameWrapper = styled.div`
  margin-left: 1rem;
  display: flex;
`;
const SOriginalEmailWrapper = styled.div`
  background: ${(props) => props.theme.palette.background.disabled};
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  padding: 10px 0;
`;
const SDeleteAllIconButton = styled(IconButton)`
  &.hide {
    display: none;
  }
`;
const SArrowForward = styled(ArrowForwardRounded)`
  align-self: center;
`;
